"use client";

import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@tosize/ui";
import { Accordion } from "radix-ui";
import type { ReactNode } from "react";

interface Props {
	items: {
		title: string;
		content: ReactNode;
	}[];
}

export function DetailsAccordion({ items }: Props) {
	return (
		<Accordion.Root
			type="multiple"
			defaultValue={items.map((_item, index) => index.toString())}
			className="space-y-6"
		>
			{items.map((item, index) => (
				<Accordion.Item value={index.toString()} key={index}>
					<Accordion.Header asChild>
						<Accordion.Trigger className="group flex w-full items-center justify-between border-b pb-2 text-left focus-visible:outline-none">
							<Typography
								variant="paragraph-bold"
								size="text-lg"
								className="group-hover:underline group-focus-visible:underline"
							>
								{item.title}
							</Typography>
							<div className="flex h-7 w-7 items-center justify-center rounded-full bg-white">
								<span className="hidden group-rdx-state-open:inline">
									<FontAwesomeIcon icon={faMinus} />
								</span>
								<span className="hidden group-rdx-state-closed:inline">
									<FontAwesomeIcon icon={faPlus} />
								</span>
							</div>
						</Accordion.Trigger>
					</Accordion.Header>
					<Accordion.Content className="pt-4">{item.content}</Accordion.Content>
				</Accordion.Item>
			))}
		</Accordion.Root>
	);
}

interface DetailsItemProps {
	title: string;
	subtitle?: string;
	content: ReactNode;
}

export function DetailsItem(props: DetailsItemProps) {
	return (
		<div className="space-y-3">
			<div className="space-y-1">
				<Typography variant="paragraph-bold" color="text-shades-dark">
					{props.title}
				</Typography>

				{props.subtitle !== undefined && (
					<Typography variant="paragraph-italic" size="text-sm" color="text-shades-dark">
						{props.subtitle}
					</Typography>
				)}
			</div>

			<div className="prose text-shades-dark">{props.content}</div>
		</div>
	);
}
